import { useCallback, useEffect, useRef } from 'react';
import { Model } from 'survey-react-ui';

import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

type SurveyData = Record<string, unknown>;

type UseSurveyUpdateQueueProps = {
  ready: boolean;
  reviewMode: boolean;
  setSurveyResults: (data: SurveyData) => void;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateSurveyResponse>[1];
};

const useSurveyUpdateQueue = ({
  ready,
  reviewMode,
  setSurveyResults,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyUpdateQueueProps) => {
  const isUpdatingRef = useRef(false);
  const nextToUpdateRef = useRef<{ answersJson: string; complete: boolean } | null>(null);
  const latestUpdatedAtRef = useRef<Date | undefined>(surveyResponseUpdatedAt);

  useEffect(() => {
    latestUpdatedAtRef.current = surveyResponseUpdatedAt;
  }, [surveyResponseUpdatedAt]);

  const runNextUpdate = useCallback(async () => {
    if (isUpdatingRef.current) return;

    const next = nextToUpdateRef.current;
    if (!next || !surveyResponseId || !ready) return;

    nextToUpdateRef.current = null;
    isUpdatingRef.current = true;

    try {
      if (!reviewMode) {
        const result = await updateSurveyAnswers({
          input: {
            id: surveyResponseId,
            answers: next.answersJson,
            complete: next.complete,
            expectedUpdatedAt: latestUpdatedAtRef.current,
          },
        });

        const updatedAt = result.data?.updateSurveyResponse?.updatedAt;
        if (updatedAt) {
          latestUpdatedAtRef.current = new Date(updatedAt);
        }

        if (next.complete) {
          setSurveyResults(JSON.parse(next.answersJson));
        }
      }
    } finally {
      isUpdatingRef.current = false;
    }

    if (nextToUpdateRef.current) void runNextUpdate();
  }, [ready, setSurveyResults, surveyResponseId, updateSurveyAnswers, reviewMode]);

  const enqueueUpdateSurvey = useCallback(
    async (survey: Model, complete: boolean) => {
      nextToUpdateRef.current = { answersJson: JSON.stringify(survey.data), complete };
      void runNextUpdate();
    },
    [runNextUpdate]
  );

  return enqueueUpdateSurvey;
};

export { useSurveyUpdateQueue };
